<template>
  <div
    v-show="showTooltip"
    :class="[$style.tooltipContainer, tooltipContainerClass, className]"
  >
    <div
      ref="icon"
      :class="$style.iconContainer"
      @mouseleave="handleMouseleave"
      @mouseover="handleMouseover"
    >
      <Component
        :is="iconComponent"
        v-if="iconComponent"
        :class="[$style.iconImg, iconClass]"
      />
      <i
        v-else
        :class="[$style.icon, iconClass]"
      />
    </div>
    <BTooltip
      ref="tooltip"
      placement="righttop"
      :show.sync="visible"
      :target="() => $refs.icon"
      triggers=""
    >
      <transition
        :enterActiveClass="$style.contentContainerTransition"
        :enterClass="$style.contentContainerEnter"
        :leaveActiveClass="$style.contentContainerTransition"
        :leaveToClass="$style.contentContainerLeaveTo"
      >
        <div
          v-show="visible"
          :class="[$style.contentContainer, contentContainerClass]"
          :style="position"
          @mouseleave="hideContentContainer"
          @mouseover="cancelContainerTimeout"
        >
          <div
            v-if="iconComponent"
            :class="[$style.iconImg, $style.tooltipIcon]"
          >
            <Component
              :is="iconComponent"
              :class="iconClass"
            />
          </div>
          <i
            v-else
            :class="[$style.tooltipIcon, iconClass]"
          />
          <div :class="$style.textContainer">
            <slot />
          </div>
        </div>
      </transition>
    </BTooltip>
    <transition
      :enterActiveClass="$style.contentContainerTransition"
      :enterClass="$style.contentContainerEnter"
      :leaveActiveClass="$style.contentContainerTransition"
      :leaveToClass="$style.contentContainerLeaveTo"
    >
      <div
        v-show="visible"
        :class="$style.overlay"
        @click.stop="hideContentContainer"
      />
    </transition>
  </div>
</template>

<script>
/**
 * We are tied to the old version of the library
 * @todo refactor this component to remove dependency
 *  */
import BTooltip from 'bootstrap-vue-old-rc/es/components/tooltip/tooltip';

export default {
  name: 'IconTooltip',

  components: {
    BTooltip,
  },

  props: {
    className: {
      type: String,
      default: '',
    },

    contentContainerClass: {
      type: [String, Array],
      default: '',
    },

    customPositioning: {
      type: Object,
      default: () => ({ x: 0, y: 0 }),
    },

    iconClass: {
      type: [String, Array],
      default: 'jc-info-circle',
    },

    /**
     * As an alternative to using the icon fonts, use the vue-svg-loader in the
     * parent component to generate a new component for an external SVG file and
     * then pass it to the IconTooltip. This technique can be used to dynamically
     * load different SVG images inline in the IconTooltip.
     *
     * @example
     * <template>
     *   <IconTooltip :iconComponent="iconComponent" />
     * </template>
     * <script>
     * import Icon from 'public/img/icons/icon.svg?inline';
     * import IconTooltip from 'client/components/IconTooltip.vue';
     *
     * export default {
     *   components: { IconTooltip },
     *   computed: {
     *     iconComponent() { return Icon; },
     *   },
     * };
     * </ script>
     */
    iconComponent: {
      type: Object,
      default: null,
    },

    mouseoverAction: {
      type: Function,
      default: () => {},
    },

    showTooltip: {
      type: Boolean,
      default: true,
    },

    tooltipContainerClass: {
      type: [String, Array],
      default: '',
    },
  },

  data: () => ({
    containerTimeoutId: null,
    iconTimeoutId: null,
    lastPageCoordinates: {
      pageX: null,
      pageY: null,
    },
    visible: false,
  }),

  computed: {
    position() {
      const positions = {};
      if (this.customPositioning.x !== 0) positions.left = `${this.customPositioning.x}px`;
      if (this.customPositioning.y !== 0) positions.top = `${this.customPositioning.y}px`;
      return positions;
    },
  },

  beforeDestroy() {
    this.handleBeforeDestroy();
  },

  methods: {
    cancelContainerTimeout() {
      window.clearTimeout(this.containerTimeoutId);
      this.containerTimeoutId = null;
    },

    handleBeforeDestroy() {
      this.visible = false;
      this.lastPageCoordinates.pageX = null;
      this.lastPageCoordinates.pageY = null;
    },

    /**
     * Clear the timeout upon mouseleave so that the container is not shown if
     * the user quickly mouses over the icon.
     */
    handleMouseleave() {
      window.clearTimeout(this.iconTimeoutId);
      this.iconTimeoutId = null;
    },

    /**
     * Set a timeout to show the container 0.5s after the mouse moves into the
     * icon, thus introducing a short delay so that the container is only shown
     * when the user deliberately focuses on the icon.
     */
    handleMouseover(event) {
      if (!this.$slots.default && !this.$scopedSlots.default) {
        return;
      }

      // Ignore mouseover event if it has the same coordidates as the previous
      // one. This ensures that the tooltip is only displayed once if the mouse
      // remains positioned over an IconTooltip that appears just above the fold.
      const { pageX, pageY } = event;

      if (pageX === this.lastPageCoordinates.pageX && pageY === this.lastPageCoordinates.pageY) {
        return;
      }

      this.lastPageCoordinates.pageX = pageX;
      this.lastPageCoordinates.pageY = pageY;

      this.iconTimeoutId = window.setTimeout(() => {
        // Set another timeout to hide the content container in 3s if the user
        // does not _focus_ on it.
        this.containerTimeoutId = window.setTimeout(() => this.hideContentContainer(), 3000);
        this.showContentContainer();
        this.mouseoverAction();
      }, 250);
    },

    hideContentContainer() {
      this.visible = false;
    },

    showContentContainer() {
      this.visible = true;
    },
  },
};
</script>

<style module>
@value (infoContentSlow, infoIconSlow) from '~css/transitions.css';
@value (tooltipOverlayZIndex, tooltipZIndex) from '~css/z-index.css';

.tooltipContainer {
  display: inline-block;
  margin-left: 10px;
  max-width: 20px;
  transition: opacity infoIconSlow ease-in;
  z-index: 550;
}

.iconContainer {
  display: inline-block;
  line-height: 1;
  z-index: 550;
}

.icon {
  color: var(--jcGrey300);
  display: inline-block;
  font-size: 11px;
}

.iconImg {
  display: inline-block;
  flex-shrink: 0;
  height: 18px;
  width: 18px;
}

.overlay {
  background: rgba(0, 0, 0, 0.1);
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: tooltipOverlayZIndex;
}

.contentContainer {
  align-items: flex-start;
  background-color: var(--jcWhite50);
  border-radius: 3px;
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.2);
  cursor: default;
  display: flex;
  justify-content: center;
  left: -34px;
  max-width: 300px;
  min-height: 57px;
  overflow: hidden;
  padding: 13px 16px;
  position: relative;
  text-align: left;
  top: -30px;
  word-break: break-word;
}

[x-placement="left"] .contentContainer {
  flex-direction: row-reverse;
  left: auto;
  right: -34px;
}

.tooltipIcon {
  color: var(--jc-color-informational-base);
}

.tooltipIcon::before {
  font-size: 22px;
  margin-top: 1.25em;
}

.textContainer {
  font-size: 11px;
  font-weight: 400;
  line-height: 1.5;
  padding-left: 12px;
  white-space: normal;
}

[x-placement="left"] .textContainer {
  padding-left: auto;
  padding-right: 12px;
}

.contentContainerEnter,
.contentContainerLeaveTo {
  opacity: 0;
}

.contentContainerTransition {
  transition: opacity infoContentSlow ease-in;
}
</style>
